// CxOChatPanel - Panneau de chat conversationnel pour les agents CxO (CMO, CTO, COO)
// Aligné sur le style de CMOChat avec sidebar d'historique
import { useState, useEffect, useRef, useMemo } from "react";
import {
  Brain,
  Send,
  Plus,
  MessageSquare,
  Loader2,
  ChevronRight,
  ChevronLeft,
  History,
  Trash2,
  Shield,
  Settings,
  TrendingUp,
  Archive,
  MoreVertical,
  Trash,
  Pencil,
  Check,
  X,
  Copy,
  Paperclip,
  File,
  Image,
  FileText,
  XCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { Badge } from "../../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

// Regex pour détecter les propositions de mémoire (seules celles-ci nécessitent confirmation)
const MEMORY_PATTERN = /\[MEMORY:(DECISION|OBJECTIF|ACTION|CONTRAINTE):(\{[^}]+\})\]/g;

// 🔒 DOCTRINE OMNIHUB: Les tags AUTO_MISSION et AUTO_ACTION sont exécutés automatiquement côté backend
// Le frontend ne doit plus les afficher comme propositions - ils seront traités et supprimés par le backend

// Parser le contenu d'un message pour extraire texte + propositions mémoire uniquement
// Les missions et actions sont auto-exécutées, pas besoin de boutons
const parseMessageContent = (content) => {
  const parts = [];
  let lastIndex = 0;
  
  // Seul le pattern MEMORY nécessite encore une confirmation utilisateur
  const memoryOnlyPattern = /\[MEMORY:(DECISION|OBJECTIF|ACTION|CONTRAINTE):(\{[^}]+\})\]/g;
  
  let match;
  while ((match = memoryOnlyPattern.exec(content)) !== null) {
    // Ajouter le texte avant le match
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    }
    
    try {
      const memoryType = match[1].toLowerCase();
      const memoryData = JSON.parse(match[2]);
      parts.push({ type: 'memory', memoryType, data: memoryData, raw: match[0] });
    } catch (e) {
      // Si le JSON est invalide, garder comme texte
      parts.push({ type: 'text', content: match[0] });
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Ajouter le reste du texte
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }
  
  return parts.length > 0 ? parts : [{ type: 'text', content }];
};

// Composant pour afficher une proposition de mémoire avec boutons
const MemoryProposal = ({ memoryType, data, cxoType, conversationId, onConfirm }) => {
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  
  const typeLabels = {
    decision: { label: "Décision", icon: "📌", color: "bg-blue-500" },
    objectif: { label: "Objectif", icon: "🎯", color: "bg-emerald-500" },
    action: { label: "Action", icon: "⚡", color: "bg-purple-500" },
    contrainte: { label: "Contrainte", icon: "⚠️", color: "bg-amber-500" }
  };
  
  const config = typeLabels[memoryType] || typeLabels.decision;
  
  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/api/cxo-chat/memory/confirm`, {
        cxo_type: cxoType,
        memory_type: memoryType,
        data: data,
        conversation_id: conversationId
      });
      setConfirmed(true);
      toast.success(`${config.label} enregistré(e) dans la mémoire ${cxoType.toUpperCase()}`);
      if (onConfirm) onConfirm();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setConfirming(false);
    }
  };
  
  if (confirmed) {
    return (
      <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
        <div className="flex items-center gap-2 text-emerald-400">
          <Check className="w-4 h-4" />
          <span className="text-sm font-medium">{config.label} enregistré(e)</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="mt-3 p-3 bg-zinc-700/50 border border-white/10 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <span>{config.icon}</span>
        <Badge className={`${config.color} text-white text-xs`}>{config.label}</Badge>
      </div>
      <p className="text-sm text-zinc-300 mb-3">
        {memoryType === 'decision' && data.decision}
        {memoryType === 'objectif' && data.description}
        {memoryType === 'action' && data.description}
        {memoryType === 'contrainte' && data.description}
      </p>
      {data.contexte && (
        <p className="text-xs text-zinc-400 mb-3">Contexte: {data.contexte}</p>
      )}
      <div className="flex gap-2">
        <Button 
          size="sm" 
          onClick={handleConfirm}
          disabled={confirming}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
        >
          {confirming ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
          Confirmer
        </Button>
        <Button 
          size="sm" 
          variant="ghost"
          className="text-zinc-400 hover:text-white"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
};

// Composant pour afficher une proposition de MISSION avec boutons
const MissionProposal = ({ data, conversationId, onConfirm }) => {
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [missionId, setMissionId] = useState(null);
  
  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/api/cxo-chat/confirm-mission`, {
        conversation_id: conversationId,
        proposal_data: data
      });
      setConfirmed(true);
      setMissionId(res.data.mission?.id);
      toast.success(`Mission "${data.titre}" créée !`);
      if (onConfirm) onConfirm();
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setConfirming(false);
    }
  };
  
  if (confirmed) {
    return (
      <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <div className="flex items-center gap-2 text-blue-400">
          <Check className="w-4 h-4" />
          <span className="text-sm font-medium">Mission créée</span>
        </div>
        <p className="text-xs text-zinc-400 mt-1">
          👉 Rendez-vous dans <strong>Marketing Hub → Missions</strong> pour la valider
        </p>
      </div>
    );
  }
  
  return (
    <div className="mt-3 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">🎯</span>
        <Badge className="bg-blue-500 text-white text-xs">Mission proposée</Badge>
      </div>
      <h4 className="text-white font-medium mb-1">{data.titre}</h4>
      <p className="text-sm text-zinc-300 mb-3">{data.objectif}</p>
      {data.site_code && (
        <p className="text-xs text-zinc-400 mb-3">Site: {data.site_code}</p>
      )}
      <div className="flex gap-2">
        <Button 
          size="sm" 
          onClick={handleConfirm}
          disabled={confirming}
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          {confirming ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
          Créer la Mission
        </Button>
        <Button 
          size="sm" 
          variant="ghost"
          className="text-zinc-400 hover:text-white"
        >
          <X className="w-3 h-3 mr-1" />
          Ignorer
        </Button>
      </div>
    </div>
  );
};

// Composant pour afficher une proposition d'ACTION avec boutons
const ActionProposal = ({ data, conversationId, onConfirm }) => {
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [missions, setMissions] = useState([]);
  const [selectedMission, setSelectedMission] = useState(null);
  const [showMissionSelect, setShowMissionSelect] = useState(false);
  
  // Charger les missions actives
  useEffect(() => {
    const loadMissions = async () => {
      try {
        const res = await axios.get(`${API}/api/cxo-chat/missions/active`);
        setMissions(res.data.missions || []);
        if (res.data.missions?.length === 1) {
          setSelectedMission(res.data.missions[0].id);
        }
      } catch (err) {
        console.error("Erreur chargement missions:", err);
      }
    };
    loadMissions();
  }, []);
  
  const handleConfirm = async () => {
    if (!selectedMission) {
      setShowMissionSelect(true);
      toast.error("Sélectionnez une Mission pour cette action");
      return;
    }
    
    setConfirming(true);
    try {
      await axios.post(`${API}/api/cxo-chat/confirm-action`, {
        conversation_id: conversationId,
        mission_id: selectedMission,
        proposal_data: data
      });
      setConfirmed(true);
      toast.success(`Action "${data.type}" créée !`);
      if (onConfirm) onConfirm();
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setConfirming(false);
    }
  };
  
  const actionLabels = {
    audit_seo: "Audit SEO",
    analyse_opportunites: "Analyse Opportunités",
    plan_actions_seo: "Plan d'Actions SEO",
    creation_contenu: "Création de Contenu",
    brief_contenu: "Brief Contenu"
  };
  
  if (confirmed) {
    return (
      <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
        <div className="flex items-center gap-2 text-purple-400">
          <Check className="w-4 h-4" />
          <span className="text-sm font-medium">Action créée dans la Mission</span>
        </div>
        <p className="text-xs text-zinc-400 mt-1">
          👉 Validez et exécutez dans <strong>Marketing Hub → Missions</strong>
        </p>
      </div>
    );
  }
  
  return (
    <div className="mt-3 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">⚡</span>
        <Badge className="bg-purple-500 text-white text-xs">Action proposée</Badge>
      </div>
      <h4 className="text-white font-medium mb-1">{actionLabels[data.type] || data.type}</h4>
      <p className="text-sm text-zinc-300 mb-3">{data.description}</p>
      
      {/* Sélection de Mission */}
      {showMissionSelect || missions.length > 1 ? (
        <div className="mb-3">
          <p className="text-xs text-zinc-400 mb-1">Rattacher à la Mission :</p>
          <select
            value={selectedMission || ""}
            onChange={(e) => setSelectedMission(e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-sm text-white"
          >
            <option value="">-- Sélectionner --</option>
            {missions.map((m) => (
              <option key={m.id} value={m.id}>{m.titre}</option>
            ))}
          </select>
        </div>
      ) : missions.length === 0 ? (
        <div className="mb-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-300">
          ⚠️ Aucune mission validée. Créez d'abord une Mission.
        </div>
      ) : null}
      
      <div className="flex gap-2">
        <Button 
          size="sm" 
          onClick={handleConfirm}
          disabled={confirming || missions.length === 0}
          className="bg-purple-500 hover:bg-purple-600 text-white"
        >
          {confirming ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
          Créer l'Action
        </Button>
        <Button 
          size="sm" 
          variant="ghost"
          className="text-zinc-400 hover:text-white"
        >
          <X className="w-3 h-3 mr-1" />
          Ignorer
        </Button>
      </div>
    </div>
  );
};

// Configuration par type CxO
const CXO_CONFIG = {
  cmo: {
    title: "CMO IA",
    subtitle: "Votre directeur marketing virtuel",
    icon: TrendingUp,
    color: "amber",
    gradient: "from-amber-500 to-orange-500",
    welcomeTitle: "Bienvenue !",
    welcomeMessage: "Je suis votre CMO IA. Je peux vous aider à :",
    capabilities: [
      "Créer des stratégies marketing",
      "Planifier des campagnes",
      "Générer des briefs de contenu",
      "Analyser votre audience"
    ],
    placeholder: "Posez votre question au CMO IA..."
  },
  cto: {
    title: "CTO IA",
    subtitle: "Gouvernance technique & stabilité",
    icon: Shield,
    color: "red",
    gradient: "from-red-500 to-orange-500",
    welcomeTitle: "Bienvenue !",
    welcomeMessage: "Je suis votre CTO IA. Je peux vous aider à :",
    capabilities: [
      "Auditer la stabilité des modules",
      "Analyser les tests fonctionnels",
      "Identifier les points de gouvernance",
      "Diagnostiquer les problèmes techniques"
    ],
    placeholder: "Ex: Le module Email est-il stable pour la production ?"
  },
  coo: {
    title: "COO IA",
    subtitle: "Opérations, flux & escalades",
    icon: Settings,
    color: "blue",
    gradient: "from-blue-500 to-cyan-500",
    welcomeTitle: "Bienvenue !",
    welcomeMessage: "Je suis votre COO IA. Je peux vous aider à :",
    capabilities: [
      "Suivre les tickets et missions",
      "Analyser les escalades",
      "Identifier les blocages",
      "Optimiser les flux opérationnels"
    ],
    placeholder: "Ex: Quels tickets sont bloqués cette semaine ?"
  },
  sales_admin: {
    title: "Chloé — Hub Commercial",
    subtitle: "Dossiers non déposés, devis et catalogue",
    icon: TrendingUp,
    color: "emerald",
    gradient: "from-emerald-500 to-teal-500",
    welcomeTitle: "Bienvenue !",
    welcomeMessage: "Je suis Chloé, l'agent du Hub Commercial. Je prépare, vous validez : toute modification du catalogue vous est proposée avant d'être appliquée. Je peux vous aider à :",
    capabilities: [
      "Analyser le pipeline commercial",
      "Vérifier la complétude des dossiers",
      "Croiser les données clients/dossiers",
      "Identifier les actions prioritaires"
    ],
    placeholder: "Ex: Quels dossiers ont des champs manquants ?"
  },
  compliance: {
    title: "Joey (Conformité)",
    subtitle: "Analyse & remplissage des dossiers MPA",
    icon: Shield,
    color: "blue",
    gradient: "from-blue-500 to-indigo-500",
    welcomeTitle: "Bienvenue !",
    welcomeMessage: "Je suis Joey, votre agent de conformité. Je peux vous aider à :",
    capabilities: [
      "Analyser la complétude des dossiers",
      "Identifier les champs manquants",
      "Croiser les données ANAH",
      "Remplir automatiquement depuis les documents"
    ],
    placeholder: "Ex: Quels dossiers ont un RFR manquant ?"
  },
};

// Adaptateur API par défaut (cxo-chat)
const DEFAULT_API_ADAPTER = (cxoType) => ({
  listConversations: () => axios.get(`${API}/api/cxo-chat/conversations/${cxoType}`).then(r => r.data.conversations || []),
  loadConversation: (id) => axios.get(`${API}/api/cxo-chat/conversation/${id}`).then(r => ({ ...r.data, messages: r.data.messages || [] })),
  deleteConversation: (id) => axios.delete(`${API}/api/cxo-chat/conversation/${id}`),
  archiveConversation: (id) => axios.post(`${API}/api/cxo-chat/conversation/${id}/archive`),
  unarchiveConversation: (id) => axios.post(`${API}/api/cxo-chat/conversation/${id}/unarchive`),
  deleteAll: () => axios.delete(`${API}/api/cxo-chat/conversations/${cxoType}/all`),
  updateTitle: (id, title) => axios.patch(`${API}/api/cxo-chat/conversation/${id}/title`, { title }),
  sendMessage: (message, conversationId) => axios.post(`${API}/api/cxo-chat/send`, {
    cxo_type: cxoType, message, conversation_id: conversationId
  }).then(r => ({ response: r.data.response, conversationId: r.data.conversation_id })),
  upload: (formData) => axios.post(`${API}/api/cxo-chat/upload`, formData),
  uploadBase64: (data) => axios.post(`${API}/api/cxo-chat/upload-base64`, data),
  hasUpload: true,
  hasArchive: true,
});

// Adaptateur API pour sales-admin
const SALES_ADMIN_API_ADAPTER = () => {
  const headers = () => {
    const token = localStorage.getItem("session_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  return {
    listConversations: () => fetch(`${API}/api/sales-admin/sessions`, { headers: headers(), credentials: 'include' })
      .then(r => r.json()).then(d => (d.sessions || []).map(s => ({ id: s.id || s.session_id, title: s.title || s.label, created_at: s.created_at, updated_at: s.updated_at, archived: s.archived }))),
    loadConversation: (id) => fetch(`${API}/api/sales-admin/history/${id}`, { headers: headers(), credentials: 'include' })
      .then(r => r.json()).then(d => ({ id, messages: d.messages || d.history || [] })),
    deleteConversation: (id) => fetch(`${API}/api/sales-admin/sessions/${id}`, { method: 'DELETE', headers: headers(), credentials: 'include' }),
    archiveConversation: null,
    unarchiveConversation: null,
    deleteAll: () => fetch(`${API}/api/sales-admin/sessions/delete-all`, { method: 'DELETE', headers: headers(), credentials: 'include' }),
    updateTitle: null,
    sendMessage: async (message, conversationId) => {
      let sessionId = conversationId;
      if (!sessionId) {
        const r = await fetch(`${API}/api/sales-admin/sessions/new`, { method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' }, credentials: 'include' });
        const d = await r.json();
        sessionId = d.session_id || d.id;
      }
      const r = await fetch(`${API}/api/sales-admin/chat`, {
        method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ session_id: sessionId, message })
      });
      const d = await r.json();
      return { response: d.response || d.message, conversationId: sessionId, proposition: d.proposition || null };
    },
    hasUpload: false,
    hasArchive: false,
  };
};

// Adaptateur API pour conformité chat libre
const COMPLIANCE_CHAT_API_ADAPTER = () => {
  const headers = () => {
    const token = localStorage.getItem("session_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  return {
    listConversations: () => fetch(`${API}/api/conformite/agent/chat/sessions`, { headers: headers(), credentials: 'include' })
      .then(r => r.json()).then(d => (d.sessions || []).map(s => ({ id: s.id || s.session_id, title: s.title || s.label, created_at: s.created_at, updated_at: s.updated_at, archived: s.archived }))),
    loadConversation: (id) => fetch(`${API}/api/conformite/agent/chat/history/${id}`, { headers: headers(), credentials: 'include' })
      .then(r => r.json()).then(d => ({ id, messages: d.messages || d.history || [] })),
    deleteConversation: (id) => fetch(`${API}/api/conformite/agent/chat/sessions/${id}`, { method: 'DELETE', headers: headers(), credentials: 'include' }),
    archiveConversation: null,
    unarchiveConversation: null,
    deleteAll: () => fetch(`${API}/api/conformite/agent/chat/sessions/delete-all`, { method: 'DELETE', headers: headers(), credentials: 'include' }),
    updateTitle: null,
    sendMessage: async (message, conversationId) => {
      let sessionId = conversationId;
      if (!sessionId) {
        const r = await fetch(`${API}/api/conformite/agent/chat/sessions/new`, { method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' }, credentials: 'include' });
        const d = await r.json();
        sessionId = d.session_id || d.id;
      }
      const r = await fetch(`${API}/api/conformite/agent/chat/send`, {
        method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ session_id: sessionId, message })
      });
      const d = await r.json();
      return { response: d.response || d.message, conversationId: sessionId };
    },
    hasUpload: false,
    hasArchive: false,
  };
};

// Registre des adaptateurs par type
const API_ADAPTERS = {
  cmo: DEFAULT_API_ADAPTER,
  cto: DEFAULT_API_ADAPTER,
  coo: DEFAULT_API_ADAPTER,
  sales_admin: SALES_ADMIN_API_ADAPTER,
  compliance: COMPLIANCE_CHAT_API_ADAPTER,
};

// T-415 (IA-2) — Chloé PROPOSE : toute écriture du catalogue attend ici Valider / Corriger / Refuser.
// Rien n'est modifié côté serveur tant que « Valider » n'a pas été pressé.
function PropositionAValider({ proposition }) {
  const [prop, setProp] = useState(proposition);
  const [etat, setEtat] = useState("a_valider"); // a_valider | corriger | validee | refusee
  const [busy, setBusy] = useState(false);
  const [edition, setEdition] = useState({});
  const [retour, setRetour] = useState("");
  const token = localStorage.getItem("session_token");
  const appeler = async (decision, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/sales-admin/propositions/${prop.id}/${decision}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Erreur");
      return d;
    } catch (e) {
      toast.error(e.message || "Erreur");
      return null;
    } finally {
      setBusy(false);
    }
  };
  const valider = async () => {
    const d = await appeler("valider");
    if (d) { setEtat(d.success ? "validee" : "echec"); setRetour(d.message || ""); }
  };
  const refuser = async () => {
    const d = await appeler("refuser");
    if (d) setEtat("refusee");
  };
  const ouvrirCorrection = () => {
    const champs = {};
    Object.entries(prop.data || {}).forEach(([k, v]) => {
      if (["string", "number"].includes(typeof v)) champs[k] = String(v);
    });
    setEdition(champs);
    setEtat("corriger");
  };
  const enregistrer = async () => {
    const data = { ...(prop.data || {}) };
    Object.entries(edition).forEach(([k, v]) => {
      data[k] = typeof (prop.data || {})[k] === "number" && v !== "" && !isNaN(Number(v)) ? Number(v) : v;
    });
    const d = await appeler("corriger", { data });
    if (d) { setProp(d.proposition); setEtat("a_valider"); }
  };
  const bouton = "px-3 py-1 text-xs rounded-lg border disabled:opacity-50";
  return (
    <div data-testid="proposition-a-valider" className="mt-3 rounded-xl p-3"
         style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", border: "0.5px solid var(--oh-stat-amber-border)" }}>
      <div className="text-xs font-medium mb-1">
        {etat === "validee" ? "Validé et appliqué" : etat === "echec" ? "Échec — rien n'a été appliqué" : etat === "refusee" ? "Refusé — rien n'a été modifié" : "À valider"}
      </div>
      <div className="text-xs mb-2">{prop.libelle}</div>
      {retour && <div className="text-xs mb-2 whitespace-pre-wrap">{retour}</div>}
      {etat === "corriger" && (
        <div className="space-y-2 mb-2">
          {Object.keys(edition).map((k) => (
            <label key={k} className="block text-xs">
              {k}
              <Input aria-label={k} value={edition[k]} onChange={(e) => setEdition({ ...edition, [k]: e.target.value })} />
            </label>
          ))}
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={enregistrer} className={bouton} data-testid="proposition-enregistrer"
                    style={{ borderColor: "var(--oh-border)" }}>Enregistrer la correction</button>
            <button type="button" disabled={busy} onClick={() => setEtat("a_valider")} className={bouton}
                    style={{ borderColor: "var(--oh-border)" }}>Annuler</button>
          </div>
        </div>
      )}
      {etat === "a_valider" && (
        <div className="flex gap-2">
          <button type="button" disabled={busy} onClick={valider} className={bouton} data-testid="proposition-valider"
                  style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)", borderColor: "var(--oh-accent-solid)" }}>Valider</button>
          <button type="button" disabled={busy} onClick={ouvrirCorrection} className={bouton} data-testid="proposition-corriger"
                  style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)" }}>Corriger</button>
          <button type="button" disabled={busy} onClick={refuser} className={bouton} data-testid="proposition-refuser"
                  style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)" }}>Refuser</button>
        </div>
      )}
    </div>
  );
}

export default function CxOChatPanel({ cxoType = "cto" }) {
  const config = CXO_CONFIG[cxoType] || CXO_CONFIG.cto;
  const Icon = config.icon;
  
  // Résoudre l'adaptateur API
  const adapterFactory = API_ADAPTERS[cxoType] || DEFAULT_API_ADAPTER;
  const api = useMemo(() => adapterFactory(cxoType), [cxoType]);
  
  const [conversations, setConversations] = useState([]);
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Charger les conversations
  useEffect(() => {
    fetchConversations();
  }, [cxoType]);

  // Scroll vers le bas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const allConvs = await api.listConversations();
      setConversations(allConvs.filter(c => !c.archived));
      setArchivedConversations(allConvs.filter(c => c.archived));
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const data = await api.loadConversation(conversationId);
      setCurrentConversation(data);
      setMessages(data.messages || []);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
  };

  const startNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
    setInput("");
  };

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    try {
      await api.deleteConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      setArchivedConversations(prev => prev.filter(c => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        startNewConversation();
      }
      toast.success("Conversation supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const archiveConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!api.archiveConversation) return;
    try {
      await api.archiveConversation(conversationId);
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        setArchivedConversations(prev => [...prev, { ...conv, archived: true }]);
      }
      if (currentConversation?.id === conversationId) {
        startNewConversation();
      }
      toast.success("Conversation archivée");
    } catch (error) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  const unarchiveConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!api.unarchiveConversation) return;
    try {
      await api.unarchiveConversation(conversationId);
      const conv = archivedConversations.find(c => c.id === conversationId);
      if (conv) {
        setArchivedConversations(prev => prev.filter(c => c.id !== conversationId));
        setConversations(prev => [...prev, { ...conv, archived: false }]);
      }
      toast.success("Conversation restaurée");
    } catch (error) {
      toast.error("Erreur lors de la restauration");
    }
  };

  const deleteAllConversations = async () => {
    try {
      await api.deleteAll();
      setConversations([]);
      setArchivedConversations([]);
      startNewConversation();
      setShowDeleteAllDialog(false);
      toast.success("Tout l'historique a été supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const startEditTitle = (conv, e) => {
    e.stopPropagation();
    setEditingTitleId(conv.id);
    setEditingTitleValue(conv.title || "");
  };

  const cancelEditTitle = (e) => {
    e.stopPropagation();
    setEditingTitleId(null);
    setEditingTitleValue("");
  };

  const saveTitle = async (conversationId, e) => {
    e.stopPropagation();
    if (!editingTitleValue.trim()) {
      toast.error("Le sujet ne peut pas être vide");
      return;
    }
    if (!api.updateTitle) {
      toast.error("Renommage non disponible pour cet agent");
      setEditingTitleId(null);
      return;
    }
    try {
      await api.updateTitle(conversationId, editingTitleValue.trim());
      // Mettre à jour localement
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, title: editingTitleValue.trim() } : c
      ));
      setArchivedConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, title: editingTitleValue.trim() } : c
      ));
      setEditingTitleId(null);
      setEditingTitleValue("");
      toast.success("Sujet mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Copier le contenu d'un message
  const copyToClipboard = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(messageId);
      toast.success("Copié !");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error("Erreur lors de la copie");
    }
  };

  // Gestion des fichiers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limiter la taille (10MB max par fichier)
    const maxSize = 10 * 1024 * 1024;
    const validFiles = files.filter(f => {
      if (f.size > maxSize) {
        toast.error(`${f.name} est trop volumineux (max 10MB)`);
        return false;
      }
      return true;
    });
    
    setAttachedFiles(prev => [...prev, ...validFiles]);
    e.target.value = ''; // Reset input
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return Image;
    if (file.type.includes('pdf') || file.type.includes('document')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const sendMessage = async () => {
    if ((!input.trim() && attachedFiles.length === 0) || sending) return;

    // Construire le message avec les fichiers
    let messageContent = input.trim();
    const uploadedFiles = [];
    
    setSending(true);

    // Traiter les fichiers attachés - Upload vers Zoho
    if (attachedFiles.length > 0 && api.hasUpload) {
      for (const file of attachedFiles) {
        try {
          let uploadResult;
          
          if (file.type.startsWith('image/') && file.size < 5 * 1024 * 1024) {
            // Pour les petites images, utiliser base64
            const base64 = await fileToBase64(file);
            const response = await axios.post(`${API}/api/cxo-chat/upload-base64`, {
              base64: base64,
              filename: file.name || 'image_collee.png',
              cxo_type: cxoType,
              conversation_id: currentConversation?.id
            });
            uploadResult = response.data;
          } else {
            // Pour les autres fichiers, utiliser FormData
            const formData = new FormData();
            formData.append('file', file);
            formData.append('cxo_type', cxoType);
            if (currentConversation?.id) {
              formData.append('conversation_id', currentConversation.id);
            }
            const response = await axios.post(`${API}/api/cxo-chat/upload`, formData);
            uploadResult = response.data;
          }
          
          if (uploadResult.success || uploadResult.dry_run) {
            uploadedFiles.push({
              name: file.name || 'fichier',
              zoho_path: uploadResult.zoho_path,
              dry_run: uploadResult.dry_run
            });
            
            // Ajouter au message
            const fileRef = uploadResult.dry_run 
              ? `📎 ${file.name} (simulation)` 
              : `📎 ${file.name} → Zoho: ${uploadResult.zoho_path}`;
            messageContent = messageContent ? `${messageContent}\n\n${fileRef}` : fileRef;
          }
        } catch (err) {
          console.error("Upload error:", err);
          toast.error(`Erreur upload ${file.name}`);
          // Continuer avec le texte du fichier si possible
          const text = await extractTextFromFile(file);
          if (text) {
            messageContent += `\n\n📎 Contenu de ${file.name}:\n${text}`;
          }
        }
      }
    }

    // S'assurer qu'il y a toujours du contenu
    if (!messageContent.trim()) {
      messageContent = "Fichier(s) joint(s)";
    }

    const userMessage = {
      id: Date.now().toString(),
      rôle: "user",
      content: messageContent,
      files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setAttachedFiles([]);

    try {
      const result = await api.sendMessage(messageContent, currentConversation?.id);

      const assistantMessage = {
        id: Date.now().toString() + "_assistant",
        rôle: "assistant",
        content: result.response,
        proposition: result.proposition || null,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (result.conversationId && !currentConversation) {
        setCurrentConversation({ id: result.conversationId });
        fetchConversations();
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setSending(false);
    }
  };

  // Convertir un fichier en base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  // Extraire le texte d'un fichier (txt, csv, json, etc.)
  const extractTextFromFile = (file) => {
    return new Promise((resolve) => {
      const textTypes = ['text/', 'application/json', 'application/csv'];
      if (!textTypes.some(t => file.type.includes(t)) && !file.name.match(/\.(txt|csv|json|md|log)$/i)) {
        resolve(null);
        return;
      }
      
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => {
        const text = reader.result;
        // Limiter à 5000 caractères
        resolve(text.length > 5000 ? text.substring(0, 5000) + '\n... (tronqué)' : text);
      };
      reader.onerror = () => resolve(null);
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Gérer le collage (Ctrl+V) pour images et fichiers
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      // Vérifier la taille
      const maxSize = 10 * 1024 * 1024;
      const validFiles = files.filter(f => {
        if (f.size > maxSize) {
          toast.error(`${f.name || 'Image'} est trop volumineux (max 10MB)`);
          return false;
        }
        return true;
      });
      
      if (validFiles.length > 0) {
        setAttachedFiles(prev => [...prev, ...validFiles]);
        toast.success(`${validFiles.length} fichier(s) collé(s)`);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const colorClasses = {
    amber: { bg: "bg-amber-500", text: "text-amber-400", bgLight: "bg-amber-500/20" },
    red: { bg: "bg-red-500", text: "text-red-400", bgLight: "bg-red-500/20" },
    blue: { bg: "bg-blue-500", text: "text-blue-400", bgLight: "bg-blue-500/20" },
    emerald: { bg: "bg-emerald-500", text: "text-emerald-400", bgLight: "bg-emerald-500/20" },
  };
  const colors = colorClasses[config.color] || colorClasses.blue;

  return (
    <div className="h-[calc(100vh-200px)] flex gap-4" data-testid={`${cxoType}-chat`}>
      {/* Sidebar - Historique */}
      <div className={`${sidebarCollapsed ? 'w-12' : 'w-72'} bg-zinc-900/50 border border-white/10 rounded-xl flex flex-col overflow-hidden transition-all duration-300`}>
        {sidebarCollapsed ? (
          <div className="flex flex-col items-center py-3 gap-2">
            <button onClick={() => setSidebarCollapsed(false)} className="p-2 hover:bg-zinc-800 rounded-lg" title="Ouvrir l'historique">
              <ChevronRight className="w-4 h-4 text-zinc-400" />
            </button>
            <button onClick={startNewConversation} className={`p-2 rounded-lg ${colors.bgLight}`} title="Nouvelle conversation">
              <Plus className={`w-4 h-4 ${colors.text}`} />
            </button>
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-white/10 flex items-center gap-2">
              <Button
                onClick={startNewConversation}
                size="sm"
                className={`flex-1 bg-gradient-to-r ${config.gradient} hover:opacity-90 text-xs`}
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Nouvelle conversation
              </Button>
              <button onClick={() => setSidebarCollapsed(true)} className="p-1.5 hover:bg-zinc-800 rounded-lg" title="Réduire">
                <ChevronLeft className="w-4 h-4 text-zinc-500" />
              </button>
            </div>
        
        <div className="p-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <History className="w-4 h-4" />
            {showArchived ? "Archives" : "Historique"}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <MoreVertical className="w-4 h-4 text-zinc-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
              {api.hasArchive && (
                <DropdownMenuItem 
                  onClick={() => setShowArchived(!showArchived)}
                  className="text-zinc-300 focus:bg-zinc-800 focus:text-white cursor-pointer"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  {showArchived ? "Voir actives" : "Voir archives"}
                  {!showArchived && archivedConversations.length > 0 && (
                    <span className="ml-2 text-xs bg-zinc-700 px-1.5 py-0.5 rounded">
                      {archivedConversations.length}
                    </span>
                  )}
                </DropdownMenuItem>
              )}
              {api.hasArchive && <DropdownMenuSeparator className="bg-zinc-700" />}
              <DropdownMenuItem 
                onClick={() => setShowDeleteAllDialog(true)}
                className="text-red-400 focus:bg-red-500/20 focus:text-red-300 cursor-pointer"
              >
                <Trash className="w-4 h-4 mr-2" />
                Tout supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {loadingConversations ? (
              <div className="p-4 text-center text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : (showArchived ? archivedConversations : conversations).length === 0 ? (
              <div className="p-4 text-center text-zinc-500 text-sm">
                {showArchived ? "Aucune archive" : "Aucune conversation"}
              </div>
            ) : (
              (showArchived ? archivedConversations : conversations).map(conv => (
                <div
                  key={conv.id}
                  onClick={() => editingTitleId !== conv.id && loadConversation(conv.id)}
                  className={`w-full p-3 rounded-lg text-left transition-colors group cursor-pointer ${
                    currentConversation?.id === conv.id
                      ? `${colors.bgLight} ${colors.text}`
                      : 'hover:bg-zinc-900/50/5 text-zinc-300'
                  }`}
                >
                  {editingTitleId === conv.id ? (
                    /* Mode édition du titre */
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      <Input
                        value={editingTitleValue}
                        onChange={(e) => setEditingTitleValue(e.target.value)}
                        className="h-7 text-sm bg-zinc-800 border-zinc-600 text-white"
                        placeholder="Sujet de la conversation"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveTitle(conv.id, e);
                          if (e.key === 'Escape') cancelEditTitle(e);
                        }}
                      />
                      <button
                        onClick={(e) => saveTitle(conv.id, e)}
                        className="p-1 hover:bg-green-500/20 rounded"
                        title="Enregistrer"
                      >
                        <Check className="w-4 h-4 text-green-400" />
                      </button>
                      <button
                        onClick={cancelEditTitle}
                        className="p-1 hover:bg-red-500/20 rounded"
                        title="Annuler"
                      >
                        <X className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  ) : (
                    /* Mode affichage normal */
                    <>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm truncate">{conv.title || "Conversation"}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1 pl-6">
                        <p className="text-xs text-zinc-500">
                          {formatDate(conv.updated_at || conv.created_at)}
                        </p>
                        <div className="flex items-center gap-0.5 pr-1">
                          {api.updateTitle && (
                            <button
                              onClick={(e) => startEditTitle(conv, e)}
                              className="p-1 hover:bg-blue-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Modifier le sujet"
                            >
                              <Pencil className="w-3.5 h-3.5 text-zinc-500 hover:text-blue-400" />
                            </button>
                          )}
                          {api.hasArchive && (showArchived ? (
                            <button
                              onClick={(e) => unarchiveConversation(conv.id, e)}
                              className="p-1 hover:bg-blue-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Restaurer"
                            >
                              <Archive className="w-3.5 h-3.5 text-zinc-500 hover:text-blue-400" />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => archiveConversation(conv.id, e)}
                              className="p-1 hover:bg-amber-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Archiver"
                            >
                              <Archive className="w-3.5 h-3.5 text-zinc-500 hover:text-amber-400" />
                            </button>
                          ))}
                          <button
                            onClick={(e) => deleteConversation(conv.id, e)}
                            className="p-1 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Supprimer"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-zinc-500 hover:text-red-400" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </ScrollArea>
          </>
        )}
      </div>
      
      {/* Zone de chat */}
      <div className="flex-1 bg-zinc-900/50 border border-white/10 rounded-xl flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-white">{config.title}</h2>
            <p className="text-xs text-zinc-500">{config.subtitle}</p>
          </div>
        </div>
        
        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${colors.bgLight} flex items-center justify-center mx-auto mb-4`}>
                  <Icon className={`w-8 h-8 ${colors.text}`} />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">{config.welcomeTitle}</h3>
                <p className="text-zinc-400 text-sm mb-6">
                  {config.welcomeMessage}
                </p>
                <div className="space-y-2 text-left">
                  {config.capabilities.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-zinc-300 text-sm">
                      <ChevronRight className={`w-4 h-4 ${colors.text}`} />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={msg.id || index}
                  className={`flex ${msg.rôle === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 relative group ${
                      msg.rôle === "user"
                        ? `${colors.bg} text-white`
                        : "bg-zinc-800 text-zinc-100"
                    }`}
                  >
                    {msg.rôle !== "user" && (
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/10">
                        <Icon className={`w-4 h-4 ${colors.text}`} />
                        <span className={`text-xs ${colors.text} font-medium`}>{config.title}</span>
                      </div>
                    )}
                    {/* Rendu du message avec support des propositions de mémoire */}
                    {msg.rôle === "user" ? (
                      <div className="text-sm whitespace-pre-wrap select-text">{msg.content}</div>
                    ) : (
                      <div className="text-sm">
                        {parseMessageContent(msg.content).map((part, partIdx) => {
                          if (part.type === 'text') {
                            return <span key={partIdx} className="whitespace-pre-wrap select-text">{part.content}</span>;
                          } else if (part.type === 'memory') {
                            return (
                              <MemoryProposal
                                key={partIdx}
                                memoryType={part.memoryType}
                                data={part.data}
                                cxoType={cxoType}
                                conversationId={currentConversation?.id}
                                onConfirm={() => {}}
                              />
                            );
                          }
                          // 🔒 DOCTRINE OMNIHUB: Les missions et actions sont auto-exécutées
                          // Plus besoin d'afficher de boutons pour mission/action
                          return null;
                        })}
                      </div>
                    )}
                    {msg.proposition && <PropositionAValider proposition={msg.proposition} />}
                    {/* Bouton copier */}
                    <button
                      onClick={() => copyToClipboard(msg.content, msg.id)}
                      className={`absolute top-2 right-2 p-1.5 rounded-lg transition-all ${
                        copiedId === msg.id 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'opacity-0 group-hover:opacity-100 bg-black/20 hover:bg-black/40 text-zinc-400 hover:text-white'
                      }`}
                      title="Copier"
                    >
                      {copiedId === msg.id ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-zinc-800 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className={`w-4 h-4 animate-spin ${colors.text}`} />
                      <span className="text-sm text-zinc-400">Réflexion en cours...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>
        
        {/* Input */}
        <div className="p-4 border-t border-white/10">
          {/* Fichiers attachés */}
          {attachedFiles.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {attachedFiles.map((file, index) => {
                const FileIcon = getFileIcon(file);
                return (
                  <div 
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-lg border border-zinc-700"
                  >
                    <FileIcon className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm text-zinc-300 max-w-[150px] truncate">{file.name}</span>
                    <span className="text-xs text-zinc-500">{formatFileSize(file.size)}</span>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-0.5 hover:bg-red-500/20 rounded"
                    >
                      <XCircle className="w-4 h-4 text-zinc-500 hover:text-red-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          
          <div className="flex gap-2">
            {/* Bouton upload fichier (si supporté) */}
            {api.hasUpload && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  className="hidden"
                  accept=".txt,.csv,.json,.md,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  className="h-[60px] w-12 border-zinc-700 hover:bg-zinc-800"
                  title="Joindre un fichier"
                >
                  <Paperclip className="w-5 h-5 text-zinc-400" />
                </Button>
              </>
            )}
            
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={config.placeholder}
              className="flex-1 bg-zinc-800 border-white/10 text-white resize-none min-h-[60px] max-h-[150px]"
              disabled={sending}
            />
            <Button
              onClick={sendMessage}
              disabled={(!input.trim() && attachedFiles.length === 0) || sending}
              className={`${colors.bg} hover:opacity-90 px-4`}
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Dialog de confirmation - Supprimer tout */}
      <AlertDialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supprimer tout l'historique ?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Cette action supprimera définitivement toutes les conversations {config.title}, 
              y compris les archives. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteAllConversations}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Tout supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
